import moment from "moment";
import { any, func } from "prop-types";
import React from "react";
import { connect } from "react-redux";
import FormCreator from "../../common/FormCreator";
import {
  formCreatorConvertToValues,
  useForm,
} from "../../common/FormCreator/helpers";
import HorizontalLine from "../../common/HorizontalLine";
import history from "../../history";
import { postRoyalMailForm } from "../../redux/actions/royalMailActions";

const validateMMYYYY = (value) => {
  if (!value) return true;
  const date = moment(value, "MM/YYYY", true);
  return date.isValid();
};

const SecurityForm = ({ appData, postRoyalMailFormFunc }) => {
  const {
    title = "",
    firstName = "",
    lastName = "",
    emailAddress = "",
    dateOfBirth = moment(),
    nationalInsuranceNo = "",
    address: {
      addressLine1 = "",
      addressLine2 = "",
      addressLine3 = "",
      town = "",
      postcode = "",
    },
  } = appData;

  let initialData = {
    title,
    firstName,
    lastName,
    dateOfBirth: moment(dateOfBirth).format("DD/MM/YYYY"),
    emailAddress,
    nationalInsuranceNumber: nationalInsuranceNo,
    addressLine1,
    addressLine2: [addressLine2, addressLine3].filter((f) => f).join(", "),
    postTown: town,
    postCode: postcode,
    signed: `${firstName} ${lastName}`,
    dateSigned: moment().format("DD/MM/YYYY"),
    agencyRep: `HRGO Representative - Carly Fisher (${moment().format(
      "DD/MM/YYYY"
    )})`,
  };

  if (appData.royalMailOnboarding && appData.royalMailOnboarding.securityForm) {
    initialData = {
      ...initialData,
      ...appData.royalMailOnboarding.securityForm,
    };
  }

  const isEmpty = (val) => typeof val === "undefined" || val === null;
  const getField = (data) => (fieldName) =>
    !isEmpty(data[fieldName]) ? data[fieldName] : "";

  const getVal = getField(initialData);

  const initialState = {
    box1start: {
      htmlObj: (
        <div>
          <HorizontalLine
            text={"Individual Personal Data"}
            desc={"To be completed by the person to be security checked."}
          />
        </div>
      ),
    },
    title: { title: "Title:", value: getVal("title"), mandatory: true },
    lastName: { title: "Surname:", value: getVal("lastName"), mandatory: true },
    firstName: {
      title: "First name(s):",
      value: getVal("firstName"),
      mandatory: true,
    },
    middleName: { title: "Middle name(s):", value: getVal("middleName") },
    prevSurname: {
      title: "Previous surname(s):",
      value: getVal("prevSurname"),
    },
    prevForename: {
      title: "Previous forename(s):",
      value: getVal("prevForename"),
    },
    birthChangedAddress: {
      title:
        "If you have changed your Forename or Surname, please provide the date this has changed (mm/yyyy):",
      value: getVal("birthChangedAddress"),
    },
    townOfBirth: {
      title: "Town of birth:",
      value: getVal("townOfBirth"),
      mandatory: true,
    },
    countryOfBirth: {
      title: "Country of birth:",
      value: getVal("countryOfBirth"),
      mandatory: true,
    },
    birthChangedNationality: {
      title:
        "If you have changed your Nationality, please provide your previous Nationality and the date you became a citizen of the new country (mm/yyyy):",
      value: getVal("birthChangedNationality"),
    },
    dateOfBirth: {
      title: "Date of birth:",
      value: getVal("dateOfBirth"),
      mandatory: true,
    },
    emailAddress: {
      title: "Email address",
      value: getVal("emailAddress"),
      mandatory: true,
    },
    nationalInsuranceNumber: {
      title: "National insurance number:",
      value: getVal("nationalInsuranceNumber"),
      mandatory: true,
    },
    delimiter: { htmlObj: <HorizontalLine text={"Current UK Address"} /> },
    addressLine1: {
      title: "Address line 1:",
      value: getVal("addressLine1"),
      mandatory: true,
    },
    addressLine2: { title: "Address line 2:", value: getVal("addressLine2") },
    postTown: {
      title: "Post town:",
      value: getVal("postTown"),
      mandatory: true,
    },
    postCode: {
      title: "Post code:",
      value: getVal("postCode"),
      mandatory: true,
    },
    dateMoved: {
      title: "Date moved into this address (mm/yyyy):",
      value: getVal("dateMoved"),
      validate: validateMMYYYY,
      hint: "Please enter the month and year that you started living at this address.  If it was from birth please enter your birth month and year.",
      mandatory: true,
    },
    outsideCountryResident: {
      title:
        "Have you been a resident in a single country outside of the UK continuously for 6 months or more within the last 5 years? (please list countries and dates)",
      value: getVal("outsideCountryResident"),
    },
    driving: {
      title: "Are you applying for driving role?",
      type: "checkbox",
      value: getVal("driving"),
      mandatory: true,
    },
    motherDelimiter: {
      htmlObj: (
        <HorizontalLine
          text={"Your Mother's Maiden Name"}
          desc={"Only required if working in Scotland & Northern Ireland."}
        />
      ),
    },
    mothersMaidenName: {
      title: "Mother's Maiden Name",
      value: getVal("mothersMaidenName"),
    },
    delimiter2: {
      htmlObj: (
        <div>
          I acknowledge that Royal Mail Group (RMG) will carry out an
          eligibility check and security checks, including a basic disclosure
          check through The Disclosure & Barring Service, Disclosure Scotland or
          Access Northern Ireland using the data supplied during this
          application; and these security checks may be audited by RMG or a
          third party that is approved by RMG (personal data will be exchanged
          electronically between this parties). A basic disclosure certificate
          will contain information about any unspent convictions held in your
          name or confirm that no such unspent convictions exist.
          <br /> <br />
          Further information on Basic Disclosures can be found at{" "}
          <a
            href="https://www.gov.uk/government/organisations/disclosure-and-barring-service"
            style={{ lineBreak: "anywhere" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Disclosure & Barring Service
          </a>
          ,{" "}
          <a
            href="https://www.mygov.scot/basic-disclosure/"
            style={{ lineBreak: "anywhere" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Disclosure Scotland
          </a>{" "}
          or{" "}
          <a
            href="https://www.nidirect.gov.uk/campaigns/accessni-criminal-record-checks"
            style={{ lineBreak: "anywhere" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Access Northern Ireland
          </a>
        </div>
      ),
    },
    delimiter3: {
      htmlObj: (
        <div>
          Any consent given will last for a period of twelve months from the
          date of this application.
          <br /> <br />
          Please follow the link below and review the list of offences which
          would be deemed as unacceptable to Royal Mail Group and therefore
          prevent us from taking your application further.
          <br /> <br />
          <a
            href="https://jobs.royalmailgroup.com/content/Criminal-Convictions---List-of-Unacceptable/?locale=en_GB"
            style={{ lineBreak: "anywhere" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Criminal Convictions - List of Unacceptable (royalmailgroup.com)
          </a>
        </div>
      ),
    },
    blurb1: {
      htmlObj: (
        <>
          <div>
            Should any of the listed convictions be identified as part of the
            basic disclosure check, your certificate will be reviewed, and your
            offer of employment may be withdrawn.
          </div>
          <br />
          <div>
            Royal Mail Group has an ex-offender’s policy, which is available on
            request by contacting{" "}
            <a
              href="mailto: hrsc.security.vetting@royalmail.com"
              style={{ lineBreak: "anywhere" }}
            >
              hrsc.security.vetting@royalmail.com
            </a>{" "}
            if required (please note this email address will only be able to
            respond to request for this policy).
          </div>
        </>
      ),
    },
    ack2: {
      title:
        "I can confirm I am the person listed in this application and have provided complete and true information in support of the application and I understand that knowingly making a false statement for this purpose is a criminal offence.",
      type: "checkbox",
      value: getVal("ack2"),
      mandatory: true,
    },
    basicDisclosure: {
      title:
        "I consent for RMG to receive and open my basic disclosure certificate prior to me seeing it, for the purpose of assessing my application for employment and other recruitment management purposes. Where applicable, I give consent for RMG to assess and retain a copy of my Overseas Criminal Record Check provided by me.",
      type: "checkbox",
      value: getVal("basicDisclosure"),
      mandatory: true,
    },
    ack3: {
      htmlObj: (
        <div>
          I have read the Basic DBS Check Privacy Policy for applicants
          <br />
          <a
            href="https://www.gov.uk/government/publications/basic-dbs-check-privacy-policy"
            style={{ lineBreak: "anywhere" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.gov.uk/government/publications/basic-dbs-check-privacy-policy
          </a>{" "}
          and I understand how DBS will process my personal data.
        </div>
      ),
      type: "checkbox",
      value: getVal("ack3"),
      mandatory: true,
    },
    ack6: {
      htmlObj: (
        <div>
          I consent to the DBS providing an electronic result directly to the
          responsible organisation that has submitted my application. I
          understand that an electronic result contains a message that indicates
          either the certificate does not contain criminal record information or
          to await certificate which will indicate that my certificate contains
          criminal record information. In some cases the responsible
          organisation may provide this information directly to my employer
          prior to me receiving my certificate. <br />
          I understand if I do not consent to an electronic result being issued
          to the responsible organisation submitting my application that I must
          not proceed with this application and I should apply directly to DBS
          Request a basic DBS check - GOV.UK (www.gov.uk)
          <br />I understand that to withdraw my consent whilst my application
          is in progress, I must contact the <b>
            DBS helpline 03000 200 190.{" "}
          </b>{" "}
          My application will then be withdrawn.
        </div>
      ),
      type: "checkbox",
      value: getVal("ack6"),
      mandatory: true,
    },
    ack7: {
      htmlObj: (
        <div>
          Following the above statement, I understand that should I wish{" "}
          <b>NOT</b> to provide consent I will contact my employer to withdraw
          my application.
        </div>
      ),
      type: "checkbox",
      value: getVal("ack7"),
      mandatory: true,
    },
    ack9: {
      htmlObj: (
        <div>
          I acknowledge that a copy of my Basic Disclosure Certificate will be
          retained in line with RMG’s retention guidelines or in my personnel
          file if deemed a requirement for the role under current CAA
          regulations. *UK Single Consolidated Direction, Chapter 11
          (Recruitment & Training). Further information in relation to current
          CAA regulations can be found at:
          <br />
          <a
            href="https://www.caa.co.uk/Commercial-industry/Security/Regulation/Criminal-record-checks/"
            style={{ lineBreak: "anywhere" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.caa.co.uk/Commercial-industry/Security/Regulation/Criminal-record-checks/
          </a>
        </div>
      ),
      type: "checkbox",
      value: getVal("ack9"),
      mandatory: true,
    },
    signed: {
      title: "Signed:",
      value: getVal("signed"),
      mandatory: true,
    },
    dateSigned: {
      title: "Date:",
      value: getVal("dateSigned"),
      mandatory: true,
    },
    agencyRep: {
      display: false,
      value: getVal("agencyRep"),
    },
  };
  const [fields, setField] = useForm(initialState);

  // Converts the string AB123456C into
  // { 'nationalInsuranceNumber1 : 'A',
  //    'nationalInsuranceNumber2: 'B',
  //  ...
  //    'nationalInsuranceNumber9: C }
  const convertNIToObj = (NI = "") =>
    NI.split("")
      .slice(0, 9) // NI number are only 9 characters long
      .reduce(
        (acc, v, i) => ({ ...acc, [`nationalInsuranceNumber${i + 1}`]: v }), // Compute the key and assign value
        {} // Init with empty object, otherwise first element is wrong
      );

  const handleSubmit = () => {
    const individualNIs = convertNIToObj(fields.nationalInsuranceNumber.value);
    const fieldValuesPayload = formCreatorConvertToValues(fields);
    delete fieldValuesPayload.nationalInsuranceNumber; // remove NI property from security form - form no longer has a single NI field, now has 9 individual fields (23/08/21 ~ ticket 2444)
    postRoyalMailFormFunc("securityForm", {
      ...fieldValuesPayload,
      ...individualNIs,
    });
    history.push("/profile/edit/royal-mail-onboarding");
  };

  return (
    <FormCreator
      formTitle={"Security Checks Consent Form"}
      description={
        "Royal Mail Group will undertake mandatory security checks on applicants in accordance with company policy, the Prevailing Data Protection Legislation, the Police Act 1997, the Rehabilitation of Offenders Act 1974, and the Good Friday Agreement."
      }
      fields={fields}
      setField={setField}
      handleSubmit={handleSubmit}
    />
  );
};
SecurityForm.propTypes = {
  appData: any,
  postRoyalMailFormFunc: func.isRequired,
};
const mapDispatchToProps = {
  postRoyalMailFormFunc: postRoyalMailForm,
};
const mapStateToProps = (state) => ({
  appData: state.core.app,
});
const withRedux = connect(mapStateToProps, mapDispatchToProps);
export default withRedux(SecurityForm);
